// ===================================================================
// components/PracticePath.tsx
// Onglet Pratiquer : "sentier sinueux" (maquette Claude Design
// "Jogolinga - Pratiquer Chemin", variante 2b). Les activités sont des
// arches posées en quinconce ; un sentier d'empreintes d'ibis serpente de
// l'une à l'autre. Deux séparateurs : En solo / À plusieurs.
//
// La maquette est dessinée pour 390 px de large : les positions sont
// recalculées à partir de la largeur réelle du conteneur.
// ===================================================================
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export interface PracticeItem {
  id: string;
  label: string;
  /** Couleur de l'arche et du pictogramme */
  bg: string;
  ic: string;
  /** Tracé SVG (viewBox 24 × 24) */
  icon: string;
  group: 'solo' | 'multi';
  locked?: boolean;
  /** Pastille rouge (ex. défis reçus en attente) */
  badge?: number;
}

interface PracticePathProps {
  items: PracticeItem[];
  onSelect: (item: PracticeItem) => void;
}

// ── Dimensions de la maquette (largeur de référence 390) ──────────
const REF_W = 390;
const ARCH_W = 128;
const ARCH_H = 66;
const TOP = 28;
const GAP = 80;      // d'une arche à la suivante
const DIV_GAP = 34;  // place réservée à un séparateur
const STEP_GAP = 14; // espacement des empreintes
const PATH_COLOR = '#C67B4E';

interface Step { x: number; y: number; a: number }

/** Empreintes alternées gauche/droite le long d'une polyligne. */
function footprints(pts: [number, number][], gap: number): Step[] {
  const out: Step[] = [];
  let acc = gap / 2;
  let n = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (!L) continue;
    const ux = (b[0] - a[0]) / L, uy = (b[1] - a[1]) / L;
    while (acc <= L) {
      const side = n++ % 2 ? 3 : -3;
      out.push({
        x: a[0] + ux * acc - uy * side,
        y: a[1] + uy * acc + ux * side,
        a: (Math.atan2(uy, ux) * 180) / Math.PI + 90,
      });
      acc += gap;
    }
    acc -= L;
  }
  return out;
}

function useWidth(ref: React.RefObject<HTMLDivElement>) {
  const [w, setW] = useState(REF_W);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => { if (el.clientWidth > 0) setW(el.clientWidth); };
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    window.addEventListener('resize', update);
    return () => { ro?.disconnect(); window.removeEventListener('resize', update); };
  }, [ref]);
  return w;
}

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const PracticePath: React.FC<PracticePathProps> = ({ items, onSelect }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const width = Math.min(useWidth(ref), 480);

  const layout = useMemo(() => {
    const k = width / REF_W;
    const archW = Math.min(ARCH_W, width * 0.36);
    const cx = (i: number) => (i % 2 ? 290 : 100) * k;

    // Séparateur avant la première activité de chaque groupe
    const firstMulti = items.findIndex(it => it.group === 'multi');
    const dividers: { before: number; label: string }[] = [];
    if (items.length) dividers.push({ before: 0, label: t('mainMenu.practicePath.solo', '◆ EN SOLO ◆') });
    if (firstMulti > 0) dividers.push({ before: firstMulti, label: t('mainMenu.practicePath.multi', '◆ À PLUSIEURS ◆') });

    let y = TOP;
    const Y = items.map((_, i) => {
      if (dividers.some(d => d.before === i)) y += DIV_GAP;
      const r = y;
      y += GAP;
      return r;
    });
    const divs = dividers.map(d => ({ y: Y[d.before] - DIV_GAP + 10, label: d.label }));
    const nodes = items.map((_, i) => ({ x: cx(i) - archW / 2, y: Y[i], w: archW, h: ARCH_H }));

    let steps: Step[] = [];
    for (let i = 0; i < items.length - 1; i++) {
      const pts: [number, number][] = [];
      const y0 = Y[i] + ARCH_H / 2, y1 = Y[i + 1] + ARCH_H / 2;
      for (let s = 0; s <= 24; s++) {
        const u = s / 24;
        pts.push([cx(i) + (cx(i + 1) - cx(i)) * u, y0 + (y1 - y0) * (3 * u * u - 2 * u * u * u)]);
      }
      steps = steps.concat(footprints(pts, STEP_GAP));
    }
    // Pas d'empreintes sous les séparateurs ni sous les arches
    steps = steps.filter(p =>
      !divs.some(d => p.y > d.y - 10 && p.y < d.y + 24)
      && !nodes.some(n => p.x > n.x - 8 && p.x < n.x + n.w + 8 && p.y - 3 > n.y - 8 && p.y - 3 < n.y + n.h + 8));

    const height = items.length ? Y[items.length - 1] + ARCH_H + 32 : 0;
    return { nodes, steps, divs, height };
  }, [items, width, t]);

  return (
    <div ref={ref} style={{ width: '100%', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ position: 'relative', width, height: layout.height, margin: '0 auto' }}>
        {layout.steps.map((s, i) => (
          <svg key={i} width="14" height="16" viewBox="-7 -11 14 16" aria-hidden="true"
            style={{ position: 'absolute', left: s.x - 7, top: s.y - 11, transform: `rotate(${s.a.toFixed(1)}deg)` }}>
            <path d="M0 4V-3M0 -3L-4.6 -8.5M0 -3V-10M0 -3L4.6 -8.5" fill="none" stroke={PATH_COLOR} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ))}

        {layout.divs.map((d, i) => (
          <div key={i} style={{ position: 'absolute', left: 40, right: 40, top: d.y, display: 'flex', alignItems: 'center', gap: 12, fontWeight: 800, fontSize: 12, letterSpacing: '.2em', color: PATH_COLOR }}>
            <span style={{ flex: 1, height: 1, background: '#D9B892' }} />
            <span style={{ whiteSpace: 'nowrap', flex: 'none' }}>{d.label}</span>
            <span style={{ flex: 1, height: 1, background: '#D9B892' }} />
          </div>
        ))}

        {items.map((it, i) => {
          const n = layout.nodes[i];
          return (
            <motion.button
              key={it.id}
              type="button"
              onClick={() => onSelect(it)}
              aria-label={it.locked ? `${it.label} (Premium)` : it.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: it.locked ? 0.75 : 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              style={{
                position: 'absolute', left: n.x, top: n.y, width: n.w, height: n.h,
                display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', justifyContent: 'center',
                boxSizing: 'border-box', border: 'none', padding: '6px 0 0', cursor: 'pointer',
                borderRadius: '64px 64px 14px 14px', background: it.bg,
                boxShadow: '0 6px 14px rgba(42,26,18,.25), inset 0 -4px 0 rgba(0,0,0,.18)',
                fontFamily: 'inherit',
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={it.ic} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={it.icon} />
              </svg>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700, fontSize: 13, lineHeight: 1.15, color: '#FFF8EC', textAlign: 'center', padding: '0 8px' }}>
                {it.locked && <LockIcon />}
                {it.label}
              </span>
              {!!it.badge && it.badge > 0 && (
                <span aria-label={t('mainMenu.practicePath.pending', { count: it.badge, defaultValue: '{{count}} en attente' })}
                  style={{ position: 'absolute', top: 2, right: 10, minWidth: 22, height: 22, borderRadius: 11, padding: '0 6px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--color-error, #E5484D), var(--color-error-dark, #B4232C))', color: '#fff', fontSize: 12, fontWeight: 800, boxShadow: '0 0 0 2px #FFF8EC, 0 0 8px rgba(239,68,68,.5)' }}>
                  {it.badge > 9 ? '9+' : it.badge}
                </span>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default PracticePath;
